import './chatInput.scss';
import {SvgIcon} from '../../icons/svgIcon';
import {Icons} from '../../icons/icons';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useStateConversation, useStateShowChatSuggestions} from '../duck/assistant-hooks';
import {useTranslation} from 'react-i18next';
import {useWithConfirmation} from '../../common/hibuModal/hibuModal';
import {
    useAiAssistantHelpModalContentEnglish,
    useAiAssistantHelpModalContentGerman,
    useAiAssistantSupportedTags
} from '../../config/config-hooks';
import {getCurrentLanguage} from '../../i18n-service';
import {AiAssistantConversationContext, CapabilityOption, DEFAULT_ASSISTANT_CONVERSATION_CONTEXT} from './types';
import {ChatInputSettings} from './chatInputSettings';
import {ChatInputCommands} from './chatInputCommands';
import {updateConversationContextAction} from '../duck/assistant-actions';
import {useDispatch} from 'react-redux';
import {TFunction} from 'i18next';

export type ChatInputProps = {
    conversationId: string;
    getAiResponse: (inputParameter: string, contextOverride?: AiAssistantConversationContext) => Promise<void>;
    getAiSuggestions?: () => void;
    welcomeMessage?: string;
    handleResetConversation: () => void;
};


const CAPABILITIES_BUILT_IN = {
    DOCUMENTS: 'documents',
    WEB: 'web',
} as const;

type BuiltInassistantCapabilities =
    (typeof CAPABILITIES_BUILT_IN)[keyof typeof CAPABILITIES_BUILT_IN];

const CAPABILITY_ORDER: BuiltInassistantCapabilities[] = [CAPABILITIES_BUILT_IN.DOCUMENTS, CAPABILITIES_BUILT_IN.WEB];

const COMMAND_MENU_PATTERN = /(?:^|\s)@(\S*)$/;

const stripCommandPrefix = (label: string): string => label.replace(/^@\s*/, '');

export const getCommandMenuMatch = (value: string): { filter: string; startIndex: number } | null => {
    const match = COMMAND_MENU_PATTERN.exec(value);
    if (match?.index === undefined) {
        return null;
    }
    return {filter: match[1], startIndex: match.index + match[0].lastIndexOf('@')};
};

export const extractBodyFromInput = (
    message: string,
): string => {
    return message.replace(/^(?:@\S*\s*)+/, '').trim();
};

export const extractCapabilitiesFromInput = (
    input: string,
    capabilities: Set<string>,
    t: TFunction<'translation', undefined>): string[] => {
    const words = input.trim().split(/\s+/);
    const result: string[] = [];

    for (const word of words) {
        if (!word.startsWith('@')) {
            break;
        }
        const capability = Array.from(capabilities).find(
            capability => t(`assistant.capabilities.${capability}.label`) === word,
        );
        if (capability) {
            result.push(capability);
        }
    }
    return result;
};

export const constructInputWithCapabilityPrefixes = (
    input: string,
    selectedCapability: string,
    capabilitiesFromInput: string[],
    configCapabilities: string[],
    t: TFunction<'translation', undefined>): string => {
    const leadingWhitespace = new RegExp(/^\s*/).exec(input)?.[0] ?? '';
    const body: string = extractBodyFromInput(input);
    const enabledCapabilities: string[] = toggleInArray(capabilitiesFromInput, selectedCapability);
    const capabilityPrefixes: string = getSortedCapabilities(enabledCapabilities, configCapabilities).map(capability => t(`assistant.capabilities.${capability}.label`)).join(' ');

    if (!capabilityPrefixes) {
        return `${leadingWhitespace}${body}`.trimEnd();
    }
    if (!body) {
        return `${leadingWhitespace}${capabilityPrefixes} `;
    }
    return `${leadingWhitespace}${capabilityPrefixes} ${body}`;
};

const getSortedCapabilities = (
    enabledCapabilities: string[],
    capabilitiesFromConfig: string[],
): string[] => [
    ...CAPABILITY_ORDER.filter(capability =>
        enabledCapabilities.includes(capability)
    ),
    ...capabilitiesFromConfig.filter(capability =>
        enabledCapabilities.includes(capability)
    ),
];

const toggleInArray = (
    array: string[],
    value: string,
): string[] => {
    if (array.includes(value)) {
        return array.filter(item => item !== value);
    }
    return [...array, value];
};

export const ChatInput = ({
                              conversationId,
                              getAiResponse,
                              welcomeMessage,
                              getAiSuggestions,
                              handleResetConversation,
                          }: ChatInputProps): React.JSX.Element => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const currentLanguage = getCurrentLanguage();

    const [input, setInput] = useState('');
    const [isCommandMenuOpen, setIsCommandMenuOpen] = useState(false);
    const [isCommandMenuFocused, setIsCommandMenuFocused] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const showSuggestions: boolean = useStateShowChatSuggestions();

    const conversation = useStateConversation(conversationId);
    const conversationContext: AiAssistantConversationContext = conversation?.context ?? DEFAULT_ASSISTANT_CONVERSATION_CONTEXT;

    const enabledCapabilities: string[] = useMemo(() =>
        [
            ...(conversationContext.enableDocuments
                ? [CAPABILITIES_BUILT_IN.DOCUMENTS]
                : []),
            ...(conversationContext.enableWebSearch
                ? [CAPABILITIES_BUILT_IN.WEB]
                : []),
            ...conversationContext.tags,
        ], [conversationContext.enableDocuments, conversationContext.enableWebSearch, conversationContext.tags]
    );

    const helpModalContentGerman: string = useAiAssistantHelpModalContentGerman();
    const helpModalContentEnglish: string = useAiAssistantHelpModalContentEnglish();
    const [modalText, setModalText] = useState<string>('');

    const supportedTags = useAiAssistantSupportedTags();
    const capabilitiesFromConfig: string[] = useMemo(() => supportedTags.map(tag => tag.name), [supportedTags]);
    const capabilitiesFromAllSources = new Set<string>([
        ...Object.values(CAPABILITIES_BUILT_IN),
        ...capabilitiesFromConfig,
    ]);

    const capabilityOptions: CapabilityOption[] = [
        ...Array.from(Object.values(CAPABILITIES_BUILT_IN)).map(type => ({
            type,
            label: t(`assistant.capabilities.${type}.label`),
            commandLabel: stripCommandPrefix(t(`assistant.capabilities.${type}.label`)),
        })),
        ...Array.from(capabilitiesFromConfig).map(type => ({
            type,
            label: '@' + t(`fieldTitle.tagsPlural.${type}.label`),
            commandLabel: t(`fieldTitle.tagsPlural.${type}.label`),
        }))
    ];

    useEffect(() => {
        switch (currentLanguage) {
            case 'en':
                setModalText(helpModalContentEnglish);
                break;
            case 'de':
                setModalText(helpModalContentGerman);
                break;
            default:
                setModalText(helpModalContentEnglish);
        }
    }, [helpModalContentEnglish, helpModalContentGerman, currentLanguage]);

    useEffect(() => {
        setInput(getSortedCapabilities(enabledCapabilities, capabilitiesFromConfig).map(capability => t(`assistant.capabilities.${capability}.label`)).join(' ') + ' ');
    }, [capabilitiesFromConfig, enabledCapabilities, t]);

    const isAssistantCapability = (value: string): boolean =>
        capabilitiesFromAllSources.has(value);

    const updateContext = (
        context: AiAssistantConversationContext,
        capabilities: string[],
    ): AiAssistantConversationContext => {
        const updatedContext = {
            ...context,
            enableDocuments: capabilities.includes(
                CAPABILITIES_BUILT_IN.DOCUMENTS,
            ),
            enableWebSearch: capabilities.includes(
                CAPABILITIES_BUILT_IN.WEB,
            ),
            tags: capabilities.filter(
                capability =>
                    capability !== CAPABILITIES_BUILT_IN.DOCUMENTS &&
                    capability !== CAPABILITIES_BUILT_IN.WEB
            ),
        };
        dispatch(updateConversationContextAction(conversationId, updatedContext));
        return updatedContext;
    };

    const checkForCommands = (value: string) => {
        const match = getCommandMenuMatch(value);
        if (match) {
            setIsCommandMenuOpen(true);
            return;
        }
        setIsCommandMenuOpen(false);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        setInput(value);
        checkForCommands(value);
    };

    const handleKeyPress = async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        inputRef.current?.focus();
        setIsCommandMenuFocused(false);
        if (isCommandMenuOpen) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setIsCommandMenuFocused(true);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setIsCommandMenuFocused(true);
                return;
            }
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            await handleSendMessage();
        }
    };

    const handleToggle = (nextShow: boolean) => {
        setIsCommandMenuOpen(nextShow);
        if (!nextShow) {
            inputRef.current?.focus();
        }
    };

    const constructInput = (selectedCapability: string | null) => {
        if (selectedCapability && isAssistantCapability(selectedCapability)) {
            const capabilitiesFromInput: string[] = extractCapabilitiesFromInput(input, capabilitiesFromAllSources, t);
            const updatedInput: string = constructInputWithCapabilityPrefixes(input, selectedCapability, capabilitiesFromInput, capabilitiesFromConfig, t);

            setInput(updatedInput);
            setIsCommandMenuOpen(false);
            inputRef.current?.focus();
        }
    };

    const handleSendMessage = async () => {
        const capabilitiesFromInput: string[] = extractCapabilitiesFromInput(input, capabilitiesFromAllSources, t);
        const updatedContext = updateContext(conversationContext, capabilitiesFromInput);

        const msg = extractBodyFromInput(input);

        const capabilityPrefixes: string = capabilitiesFromInput.map(capability => t(`assistant.capabilities.${capability}.label`)).join(' ');
        setInput(capabilityPrefixes + ' ');
        setIsCommandMenuOpen(false);

        try {
            await getAiResponse(msg, updatedContext);
            if (getAiSuggestions && showSuggestions) {
                getAiSuggestions();
            }
        } catch (err) {
            console.error('Error:', err);
        }
    };

    const {onClick: handleOpenHelp} = useWithConfirmation(modalText, {
        backdrop: true,
        keyboard: true,
        closeButton: false,
        size: 'lg' as const,
        title: t('search.details.hibu-assistant.help.popup.title'),
        ok: t('general.close'),
        dialogClassName: 'assistant-help-modal',
        useMarkdown: true,
    });

    return (
        <div className="assistant-chat-input">
            <div className="assistant-input-wrapper">
                <div className="assistant-input-textarea-wrapper">
                    <textarea
                        disabled={false}
                        readOnly={false}
                        ref={inputRef}
                        value={input}
                        onChange={e => {
                            handleInputChange(e);
                        }}
                        onKeyDown={handleKeyPress}
                        placeholder={welcomeMessage}
                        className="assistant-input-field"
                    />
                    <ChatInputCommands options={capabilityOptions} onSelectCommand={constructInput}
                                       isOpen={isCommandMenuOpen} setFocus={isCommandMenuFocused}
                                       onToggle={handleToggle}></ChatInputCommands>
                </div>
                <div className="assistant-input-buttons-wrapper">
                    <ChatInputSettings
                        handleReset={handleResetConversation}
                        handleHelp={handleOpenHelp}
                        resetLabel={t('search.details.hibu-assistant.delete')}
                        helpLabel={t('search.details.hibu-assistant.help')}
                    />
                    <SvgIcon icon={Icons.SEND} onClick={handleSendMessage} id="icon-settings" className="icon"/>
                </div>
            </div>
            <div className="assistant-input-disclaimer">{t('assistant.input-disclaimer')}</div>
        </div>
    );
};
